import { useQuery } from '@tanstack/react-query';
import { X, Filter } from 'lucide-react';
import { useState } from 'react';
import { fetchComplaintsByFactory, type ComplaintStatus } from '@/core/api';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { ComplaintCard } from './ComplaintCard';
import { ComplaintDetailDialog } from './ComplaintDetailDialog';
import { ComplaintStatusBadge } from './ComplaintStatusBadge';
import { CustomerSelector } from '@/shared/components/selectors';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';

/**
 * Filterable list of customer complaints.
 */
export const ComplaintsList = () => {
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();

  const [statusFilter, setStatusFilter] = useState<ComplaintStatus | null>(null);
  const [customerFilter, setCustomerFilter] = useState<string | null>(null);
  const [selectedComplaintId, setSelectedComplaintId] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const { data: complaints = [], isLoading } = useQuery({
    queryKey: ['customer-complaints', selectedTenantId, selectedFactoryId, statusFilter, customerFilter],
    queryFn: () => {
      if (!selectedFactoryId) return [];
      return fetchComplaintsByFactory(selectedFactoryId, {
        status: statusFilter || undefined,
        customerId: customerFilter || undefined,
      });
    },
    enabled: !!selectedFactoryId,
  });

  const statuses: Array<{ value: ComplaintStatus; label: string }> = [
    { value: 'draft', label: 'Draft' },
    { value: 'complaint_recorded', label: 'Complaint Recorded' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'closed', label: 'Closed' },
  ];

  const activeFiltersCount = [statusFilter, customerFilter].filter(Boolean).length;

  const clearFilters = () => {
    setStatusFilter(null);
    setCustomerFilter(null);
  };

  const handleEditComplaint = (complaintId: string) => {
    setSelectedComplaintId(complaintId);
    setDetailDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Filters - Minimalistic one-row layout */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Status Filter Dropdown */}
        <div className="w-48">
          <Select
            value={statusFilter || '__ALL__'}
            onValueChange={(val) => setStatusFilter(val === '__ALL__' ? null : (val as ComplaintStatus))}
          >
            <SelectTrigger>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <SelectValue placeholder="All statuses">
                  {statusFilter ? statuses.find(s => s.value === statusFilter)?.label : 'All statuses'}
                </SelectValue>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">
                <span>All statuses</span>
              </SelectItem>
              {statuses.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  <div className="flex items-center gap-2">
                    <ComplaintStatusBadge status={status.value} />
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Customer Filter */}
        <div className="w-64">
          <CustomerSelector
            value={customerFilter}
            onValueChange={setCustomerFilter}
            allowClear
            placeholder="All customers"
          />
        </div>

        {/* Clear Filters */}
        {activeFiltersCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-4 w-4 mr-2" />
            Clear
          </Button>
        )}

        {/* Results Count */}
        <div className="ml-auto text-sm text-muted-foreground">
          {complaints.length} {complaints.length === 1 ? 'complaint' : 'complaints'}
        </div>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : complaints.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>No complaints recorded yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {complaints.map((complaint) => (
            <ComplaintCard
              key={complaint.id}
              complaint={complaint}
              onEdit={handleEditComplaint}
            />
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <ComplaintDetailDialog
        complaintId={selectedComplaintId}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
      />
    </div>
  );
};
